import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { LowEnrollmentAlert } from '../lessons/LowEnrollmentAlert';
import { CancelTermLessonModal } from '../lessons/CancelTermLessonModal';
import { useLowEnrollmentLessons } from '../../hooks/useLowEnrollmentLessons';
import { Lesson } from '@court-sync/shared';

interface LowEnrollmentSectionProps {
  academyId: string;
  lessons: Lesson[];
}

export const LowEnrollmentSection: React.FC<LowEnrollmentSectionProps> = ({
  academyId,
  lessons,
}) => {
  const {
    alerts,
    isLoading,
    fetchLowEnrollmentLessons,
    cancelTermLesson,
  } = useLowEnrollmentLessons(academyId);

  const navigate = useNavigate();
  const [showCancelTermModal, setShowCancelTermModal] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [cancelTermSubmitting, setCancelTermSubmitting] = useState(false);

  // Fetch low enrollment lessons on mount and when academyId changes
  useEffect(() => {
    fetchLowEnrollmentLessons();
  }, [academyId, fetchLowEnrollmentLessons]);

  const handleViewLesson = (lessonId: string) => {
    navigate('/admin/calendar', { state: { highlightLessonId: lessonId } });
  };

  const handleCancelTermClick = (lessonId: string) => {
    const lesson = lessons.find((l) => l.id === lessonId);
    if (lesson) {
      setSelectedLesson(lesson);
      setShowCancelTermModal(true);
    }
  };

  const handleCancelTermConfirm = async (
    reason: string,
    notifyStudents: boolean,
    grantMakeupCredit: boolean
  ) => {
    if (!selectedLesson || !academyId) return;

    setCancelTermSubmitting(true);
    try {
      await cancelTermLesson(selectedLesson.id, reason, notifyStudents, grantMakeupCredit);
      setShowCancelTermModal(false);
      setSelectedLesson(null);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to cancel term lesson'
      );
    } finally {
      setCancelTermSubmitting(false);
    }
  };

  const alertContent = (
    <LowEnrollmentAlert
      alerts={alerts}
      isLoading={isLoading}
      onViewLesson={handleViewLesson}
      onCancelTerm={handleCancelTermClick}
    />
  );

  return (
    <>
      {alertContent && <div className="mb-6">{alertContent}</div>}

      <CancelTermLessonModal
        isOpen={showCancelTermModal}
        lesson={selectedLesson}
        academyId={academyId}
        isLoading={cancelTermSubmitting}
        onCancel={() => {
          setShowCancelTermModal(false);
          setSelectedLesson(null);
        }}
        onConfirm={handleCancelTermConfirm}
      />
    </>
  );
};

export default LowEnrollmentSection;
